import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { PreviewData, SearchFilters } from "@/lib/types";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { PreviewCard } from "@/components/preview/preview-card";
import { useMobile } from "@/hooks/use-mobile";
import { PreviewCardSkeleton } from "@/components/preview/preview-card-skeleton";
import { AdvancedSearch } from "@/components/preview/advanced-search";
import {
  getAllUserTags,
  searchPreviews,
  getUserProjectsForFilter,
} from "@/services/preview-service";
import { BUCKET_NAME } from "@/lib/constants";
import { useTranslation } from "react-i18next";

// Global variable for max items to show
const MAX_PREVIEWS = 500;
const PAGE_SIZE = 3;

export default function PreviewsList() {
  const { t } = useTranslation("common");
  const [previews, setPreviews] = useState<PreviewData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableProjects, setAvailableProjects] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [totalResults, setTotalResults] = useState(0);
  const observer = useRef<IntersectionObserver | null>(null);
  const isMobile = useMobile();
  const loadingRef = useRef(false);

  useEffect(() => {
    fetchPreviews();
    loadMetadata();
  }, []);

  useEffect(() => {
    // Reset and refetch when search filters change
    setPage(0);
    setHasMore(true);
    setPreviews([]);
    fetchPreviews();
  }, [searchFilters]);

  const loadMetadata = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const [tags, projects] = await Promise.all([
        getAllUserTags(user?.id),
        getUserProjectsForFilter(user?.id),
      ]);
      setAvailableTags(tags);
      setAvailableProjects(projects);
    } catch (error) {
      console.error("Error loading metadata:", error);
    }
  };

  const fetchPreviews = async (loadMore = false) => {
    // Prevent multiple simultaneous fetch operations
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const currentPage = loadMore ? page + 1 : 0;

      loadMore ? setIsLoadingMore(true) : setIsLoading(true);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Use the new enhanced search function
      const searchResult = await searchPreviews(
        {
          filters: searchFilters,
          page: currentPage,
          pageSize: PAGE_SIZE,
          sortBy: "created_at",
          sortOrder: "desc",
        },
        user?.id
      );

      const { data, total, hasMore: hasMoreData } = searchResult;

      // Transform the data to use public URLs for assets
      const previewsWithPublicUrls =
        data?.map((preview: PreviewData) => {
          if (preview.asset_path) {
            const {
              data: { publicUrl },
            } = supabase.storage
              .from(BUCKET_NAME)
              .getPublicUrl(preview.asset_path);

            return { ...preview, asset_path: publicUrl };
          }
          return preview;
        }) || [];

      // Check if we've reached the maximum allowed previews
      const totalLoaded = loadMore
        ? previews.length + previewsWithPublicUrls.length
        : previewsWithPublicUrls.length;

      const reachedMax = totalLoaded >= MAX_PREVIEWS;

      // Set total results and check if we have more data
      setTotalResults(total);
      setHasMore(hasMoreData && !reachedMax);

      // Update previews - prevent duplicates by checking IDs
      if (loadMore) {
        setPreviews((prev) => {
          // Get IDs of existing previews for duplicate checking
          const existingIds = new Set(prev.map((p) => p.id));
          // Filter out any duplicates from new data
          const newPreviews = previewsWithPublicUrls.filter(
            (p) => !existingIds.has(p.id)
          );

          return [...prev, ...newPreviews];
        });
      } else {
        setPreviews(previewsWithPublicUrls);
      }

      if (loadMore) {
        setPage(currentPage);
      }
    } catch (err) {
      console.error("Error fetching previews:", err);
      setError("Failed to load previews. Please try again later.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      loadingRef.current = false;
    }
  };

  const handlePreviewDelete = (id: string) => {
    setPreviews((prevPreviews) =>
      prevPreviews.filter((preview) => preview.id !== id)
    );
  };

  const handlePreviewEdit = (updatedPreview: PreviewData) => {
    setPreviews((prevPreviews) =>
      prevPreviews.map((preview) =>
        preview.id === updatedPreview.id ? updatedPreview : preview
      )
    );
  };

  const lastPreviewRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading || isLoadingMore || !hasMore) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          // Only fetch more if entry is intersecting, we're not already loading, and we have more items
          if (entries[0].isIntersecting && !loadingRef.current && hasMore) {
            fetchPreviews(true);
          }
        },
        {
          // Add threshold to make sure we're further into the element before loading more
          threshold: 0.5,
        }
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, isLoadingMore, hasMore]
  );

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 container mx-auto px-4 py-6">
        {" "}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">
              {isMobile
                ? t("previewsList.header.mobileTitle")
                : t("previewsList.header.title")}
            </h1>
            {totalResults > 0 && !isLoading && (
              <p className="text-sm text-muted-foreground mt-1">
                {t("previewsList.header.results", {
                  count: totalResults,
                  filters:
                    Object.keys(searchFilters).length > 0
                      ? t("previewsList.header.filters")
                      : "",
                })}
              </p>
            )}
          </div>
          <Button asChild>
            <Link to="/models">
              <PlusCircle className="mr-2 h-4 w-4" />
              {isMobile
                ? t("previewsList.header.newPreviewMobile")
                : t("previewsList.header.newPreview")}
            </Link>
          </Button>
        </div>
        {/* Advanced Search */}
        <div className="mb-6">
          <AdvancedSearch
            filters={searchFilters}
            onFiltersChange={setSearchFilters}
            availableTags={availableTags}
            availableProjects={availableProjects}
            placeholder={t("previewsList.filter.placeholder")}
          />
        </div>
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <PreviewCardSkeleton key={i} />
              ))}
          </div>
        )}
        {error && (
          <p className="text-red-500 text-center py-8">
            {t("previewsList.error")}
          </p>
        )}{" "}
        {!isLoading && !error && previews.length === 0 && (
          <div className="bg-muted/50 rounded-lg p-8 text-center">
            {Object.keys(searchFilters).length > 0 ? (
              <>
                <p className="mb-4">{t("previewsList.empty.filtered")}</p>
                <Button
                  variant="outline"
                  onClick={() => setSearchFilters({})}
                  className="mr-2"
                >
                  {t("previewsList.empty.clearFilters")}
                </Button>
                <Button asChild>
                  <Link to="/models">
                    <PlusCircle className="mr-2 h-4 w-4" />
                    {t("previewsList.empty.createPreview")}
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <p className="mb-4">{t("previewsList.empty.noPreviews")}</p>
                <Button asChild>
                  <Link to="/models">
                    <PlusCircle className="mr-2 h-4 w-4" />
                    {t("previewsList.empty.createPreview")}
                  </Link>
                </Button>
              </>
            )}
          </div>
        )}
        {!isLoading && !error && previews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {previews.map((preview, index) => {
              // Create a truly unique key by combining index and ID
              const uniqueKey = `${preview.id}_${index}`;

              if (previews.length === index + 1) {
                return (
                  <div ref={lastPreviewRef} key={`block_${uniqueKey}`}>
                    <PreviewCard
                      key={uniqueKey}
                      preview={preview}
                      onDelete={handlePreviewDelete}
                      onEdit={handlePreviewEdit}
                      showProjectTag={true} // Show project tags in all previews
                    />
                  </div>
                );
              } else {
                return (
                  <PreviewCard
                    key={uniqueKey}
                    preview={preview}
                    onDelete={handlePreviewDelete}
                    onEdit={handlePreviewEdit}
                    showProjectTag={true} // Show project tags in all previews
                  />
                );
              }
            })}

            {isLoadingMore && (
              <>
                {Array(3)
                  .fill(0)
                  .map((_, i) => (
                    <PreviewCardSkeleton key={`loading-more-${i}`} />
                  ))}
              </>
            )}
          </div>
        )}
        {!isLoading && !isLoadingMore && !hasMore && previews.length > 0 && (
          <p className="text-center text-muted-foreground mt-8">
            {t("previewsList.noMore")}
          </p>
        )}
      </main>
    </div>
  );
}
